import { TableCell, TableRow, Avatar, AvatarFallback, Button, Checkbox } from "@libs/zapi-ui/shadcn";
import { TUser } from "../services/schemas";
import { useRevokeInvitation } from "../services/useRevokeInvitation";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { getInitials } from "../lib/getInitials";
import { formatStatus, memberRoles } from "../const/const";
import { useResendInvitation } from "../services/useResendInvitation";

type TeamTableRowProps = {
  member: TUser;
  organizationId: number;
  selectedRows: number[];
  handleSelectRow: (id: number) => void;
  currentUserId?: number;
  handleOpenManageAccess: (member: TUser) => void;
};

export function TeamTableRow(props: TeamTableRowProps) {
  const { member, organizationId, selectedRows, handleSelectRow, currentUserId, handleOpenManageAccess } = props;
  const queryClient = useQueryClient();
  const { mutate: cancelInvitation, isPending: isLoadingCancel, isSuccess: isSuccessCancel } = useRevokeInvitation();
  const { mutate: resendInvitation, isPending: isLoadingResend } = useResendInvitation();

  const isCurrentUser = member.id === currentUserId;
  const isPending = member.status === "pending";
  const actionsDisabled = isCurrentUser || isPending;

  function handleCancelInvitation() {
    if (!organizationId) return;
    cancelInvitation(
      { organizationId, email: member.email },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["organizationData", organizationId] });
          toast.success("Invitation canceled successfully");
        },
        onError: (error) => {
          toast.error(error.response?.data.message ?? "Failed to cancel invitation");
        }
      }
    );
  }

  function handleResendInvitation() {
    if (!organizationId) return;
    resendInvitation(
      { organizationId, email: member.email },
      {
        onSuccess: () => {
          toast.success("Invitation resent successfully");
        },
        onError: (error) => {
          toast.error(error.response?.data.message ?? "Failed to resend invitation");
        }
      }
    );
  }

  if (isSuccessCancel) return null;

  return (
    <TableRow data-state={selectedRows.includes(member.id) && "selected"}>
      <TableCell>
        <Checkbox
          checked={selectedRows.includes(member.id)}
          onCheckedChange={() => handleSelectRow(member.id)}
          aria-label={`Select row for ${member.firstName} ${member.lastName}`}
          disabled
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-3">
          <Avatar>
            <AvatarFallback>{getInitials(member.firstName, member.lastName)}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">
              {member.firstName} {member.lastName} {isCurrentUser && "(You)"}
            </div>
            <div className="text-sm text-muted-foreground">{member.email}</div>
          </div>
        </div>
      </TableCell>
      <TableCell>{memberRoles.find((role) => role.value === member.role)?.label || member.role}</TableCell>
      <TableCell>{member.projects?.length || 0} projects</TableCell>
      <TableCell>{formatStatus[member.status as keyof typeof formatStatus] || member.status}</TableCell>
      {/* <TableCell>-</TableCell> */}
      <TableCell>{member.createdAt.toLocaleDateString()}</TableCell>
      <TableCell>
        {isPending ? (
          <div className="flex items-center space-x-2">
            <Button
              variant="secondary"
              size="sm"
              className="bg-zinc-200 w-[120px]"
              disabled={isLoadingCancel || isLoadingResend}
              onClick={handleResendInvitation}
            >
              {isLoadingResend ? "Resending..." : "Resend Invite"}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="bg-red-500 text-white w-[120px]"
              onClick={handleCancelInvitation}
              disabled={isLoadingCancel || isLoadingResend}
            >
              {isLoadingCancel ? "Cancelling..." : "Cancel invite"}
            </Button>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <Button
              variant="secondary"
              size="sm"
              className="bg-zinc-200 w-[120px]"
              onClick={() => handleOpenManageAccess(member)}
              disabled={actionsDisabled || isLoadingResend}
            >
              Manage Access
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}
